import { useMemo } from 'react';
import {
  DepartmentTreeNodeDTO,
  useGetCollectionsForDepartment,
  useGetCurrentDepartmentTree,
} from '@/api/trafikportalen';
import { COLLECTION_TYPE_LABELS } from '@/helpers/collectionTypes';
import { findDepartmentByCollectionType } from '@/hooks/useGetStudentCollection';
import { useCollectionClaimWarning } from '@/hooks/useCollectionClaimWarning';
import { useAccessibleStudentCategories } from '@/hooks/useAccessibleStudentCategories';
import type { StudentCollectionType } from '@/helpers/collectionTypes';
import type { MessageDescriptor } from 'react-intl';

interface CategoryWarning {
  collectionType: StudentCollectionType;
  label: MessageDescriptor;
}

const useCategoryWarning = (
  departmentTree: DepartmentTreeNodeDTO | undefined,
  collectionType: StudentCollectionType | undefined,
): CategoryWarning | null => {
  const department = useMemo(() => {
    if (!departmentTree || !collectionType) return null;
    return findDepartmentByCollectionType(departmentTree, collectionType);
  }, [departmentTree, collectionType]);

  const { data: collections } = useGetCollectionsForDepartment(
    department?.id ?? '',
    { query: { enabled: !!department?.id } },
  );

  const collectionId = useMemo(() => {
    if (!collections || !collectionType) return null;
    return collections.find((c) => c.type === collectionType)?.id ?? null;
  }, [collections, collectionType]);

  const { hasWarning } = useCollectionClaimWarning(collectionId);

  return collectionType && hasWarning
    ? { collectionType, label: COLLECTION_TYPE_LABELS[collectionType] }
    : null;
};

export const useStudentCategoryWarnings = () => {
  const { data: departmentTree } = useGetCurrentDepartmentTree();
  const accessibleTypes = useAccessibleStudentCategories({
    requireWriteAccess: true,
  });

  const w0 = useCategoryWarning(departmentTree, accessibleTypes[0]);
  const w1 = useCategoryWarning(departmentTree, accessibleTypes[1]);
  const w2 = useCategoryWarning(departmentTree, accessibleTypes[2]);

  return useMemo(
    () => [w0, w1, w2].filter((w): w is CategoryWarning => w !== null),
    [w0, w1, w2],
  );
};
